import type { FunctionComponent } from "react";
import type React from "react";
import { useMemo } from "react";

import { renderToStaticMarkup } from "react-dom/server";
import styled from "styled-components";

import { ColumnChartGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes, colorsSets } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { percentageSignOnlyFilter } from "app/filters/ngFilters";

import type { IAgeChartDistributionData } from "../../types";

export const X_AXIS_AGE = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

const ChartWrapper = styled(Flex)`
    & > * {
        flex-grow: 1;
    }
`;

interface IAgeDistributionBarChartProps extends IAgeChartDistributionData {
    chartRef?: React.RefObject<HTMLDivElement>;
}

export const AgeDistributionBarChart: FunctionComponent<IAgeDistributionBarChartProps> = ({
    chartRef,
    ...ageData
}: IAgeDistributionBarChartProps) => {
    const chartData = useMemo(() => {
        const augmentedProps = Object.values(ageData).map((val) => {
            return { y: val, color: colorsSets.b1["blue 400"] };
        });

        return [{ data: augmentedProps }];
    }, [ageData]);

    return (
        <ChartWrapper
            ref={chartRef}
            padding="24px"
            width="100%"
            height="100%"
        >
            <ColumnChartGraph
                shouldPresentLegend={false}
                data={chartData}
                graphConfig={{
                    chart: {
                        height: 175,
                    },
                    tooltip: {
                        enabled: false,
                    },
                    xAxis: {
                        categories: X_AXIS_AGE,
                        labels: {
                            style: {
                                color: colorsPalettes.carbon[400],
                            },
                        },
                        lineColor: colorsPalettes.carbon[50],
                    },
                    yAxis: {
                        title: {
                            text: null,
                        },
                        labels: {
                            formatter: function () {
                                // @ts-expect-error FIXME: Argument of type 'string | number' is not assignable to parameter of type 'number | null | undefined'
                                return this.value === 0 ? "0%" : percentageSignOnlyFilter()(this.value, 0);
                            },
                            style: {
                                color: colorsPalettes.carbon[400],
                            },
                        },
                        gridLineColor: colorsPalettes.carbon[50],
                    },
                    plotOptions: {
                        series: {
                            states: {
                                hover: {
                                    enabled: false,
                                },
                            },
                        },
                        column: {
                            enableMouseTracking: false,
                            states: {
                                hover: {
                                    enabled: false,
                                },
                            },
                            groupPadding: 0.45,
                            grouping: true,
                            maxPointWidth: 48,
                            minPointLength: 3,
                            pointPadding: 0.3,
                            pointWidth: 80,
                            stacking: null,
                            dataLabels: {
                                enabled: true,
                                useHTML: true,
                                formatter() {
                                    return renderToStaticMarkup(
                                        <Text
                                            fontVariant="dataTable2"
                                            color={colorsPalettes.carbon["250"]}
                                            data-automation="age-distribution-bar-chart-value"
                                        >
                                            {percentageSignOnlyFilter()(this.y, 0)}
                                        </Text>,
                                    );
                                },
                            },
                        },
                    },
                }}
            />
        </ChartWrapper>
    );
};
